import { z } from 'zod';
import { clientPlatformSchema } from './common.js';

/** One downloadable runtime component (umu-launcher or GE-Proton). */
export const runtimeComponentSchema = z.object({
  version: z.string(),
  url: z.string().url(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  /** Bytes, so the client can show progress before the first byte arrives. */
  sizeBytes: z.number().int().nullable(),
});
export type RuntimeComponent = z.infer<typeof runtimeComponentSchema>;

/**
 * `GET /v1/app/config`. Everything here can change without shipping a new app build:
 * Proton and umu versions, minimum app versions, and the community embed mode from VERIFY-6.
 */
export const appConfigSchema = z.object({
  runtimes: z.object({
    umu: runtimeComponentSchema,
    proton: runtimeComponentSchema,
  }),
  minimumVersions: z.record(clientPlatformSchema, z.string()),
  community: z.object({
    embedMode: z.record(clientPlatformSchema, z.enum(['embedded', 'window', 'browser'])),
  }),
  downloads: z.object({
    /** Chunk URL batch size the client should ask for. Mirrors SIGNED_URL_BATCH_MAX. */
    urlBatchSize: z.number().int(),
    /** Ask for more URLs when fewer than this many unused ones remain. */
    urlBatchRefillAt: z.number().int(),
    defaultConcurrency: z.number().int(),
  }),
  discordUrl: z.string().url().nullable(),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

/**
 * What an admin may change at runtime. Written by hand rather than derived, so a field can only
 * become editable on purpose.
 */
export const appConfigPatchSchema = z
  .object({
    runtimes: z
      .object({
        umu: runtimeComponentSchema.optional(),
        proton: runtimeComponentSchema.optional(),
      })
      .partial()
      .optional(),
    minimumVersions: z.record(clientPlatformSchema, z.string()).optional(),
    community: z
      .object({
        embedMode: z
          .record(clientPlatformSchema, z.enum(['embedded', 'window', 'browser']))
          .optional(),
      })
      .optional(),
    downloads: z
      .object({
        urlBatchSize: z.number().int().min(1).max(5000).optional(),
        urlBatchRefillAt: z.number().int().min(1).max(5000).optional(),
        defaultConcurrency: z.number().int().min(4).max(32).optional(),
      })
      .optional(),
    discordUrl: z.string().url().nullable().optional(),
  })
  .strict();
export type AppConfigPatch = z.infer<typeof appConfigPatchSchema>;

/**
 * One downloadable build of the app itself, resolved from the release manifests.
 *
 * `sizeBytes` and `sha256` are only recorded for Android: the Tauri updater manifest carries a
 * signature and a URL and nothing else.
 */
export const appReleaseSchema = z.object({
  version: z.string(),
  url: z.string().url(),
  sizeBytes: z.number().int().nullable(),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
});
export type AppRelease = z.infer<typeof appReleaseSchema>;

/**
 * `GET /v1/app/releases`. What the download buttons point at, one direct link per platform.
 *
 * Null means nothing has been published for that platform yet, which the page shows rather than
 * offering a link to a file that is not there.
 */
export const appReleasesSchema = z.object({
  windows: appReleaseSchema.nullable(),
  linux: appReleaseSchema.nullable(),
  android: appReleaseSchema.nullable(),
});
export type AppReleases = z.infer<typeof appReleasesSchema>;

export const androidLatestSchema = z.object({
  versionCode: z.number().int(),
  versionName: z.string(),
  apkUrl: z.string().url(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  sizeBytes: z.number().int().nullable(),
  notes: z.string().nullable(),
});
export type AndroidLatest = z.infer<typeof androidLatestSchema>;
